import {
  Button,
  Flag,
  Icon,
  ModalActions,
  ModalContent,
  ModalHeader,
  Table,
  TableBody,
  TableBodyCell,
  TableHead,
  TableHeadCell,
  TableRow,
  ToastNotifications,
} from '@lightspeed/design-system-react'
import { T, useT } from '@transifex/react'
import {
  ApiRetailerPaymentType,
  GiftCard,
  GiftCardTransaction,
  User,
} from '@vend/types'
import {
  DATE_MEDIUM,
  DATE_TIME_SHORT,
  fetchApi,
  formatDateDistance,
  formatDateTime,
} from '@vend/utilities'
import React, { MouseEventHandler, useEffect, useState } from 'react'
import { vn } from 'vend-number'
import { useRegisterManager, useUserManager } from '../../../ng-compat/hooks'
import { useFormatCurrencyValue } from '../../sale/summary-panel/useFormatCurrencyValue'
import {
  useDisplayStatus,
  useGetDisplayTransactionType,
  useGiftCardProviderSettings,
  useViewDetailsStrings,
} from '../hooks'
import { calculateAndSortTransactionsBalance } from '../utils'
import { SalePayment } from '../../sale/payment-panel/PaymentModal/types'
import { useBigCommerceGiftCerts } from '../useBigCommerceGiftCerts'
import { useRedeemGiftCard } from '../useRedeemGiftCard'
import { selectSaleFinals } from '../../../state/slices/currentSale/currentSaleSlice'
import { useAppSelector } from '../../../state/hooks'
import {
  GiftCardModalFlow,
  GiftCardModalState,
  GiftCardType,
} from './GiftCardModal'

// Hydrated with balance and user at each transaction
export interface HydratedGiftCardTransaction extends GiftCardTransaction {
  user?: User
  balance: number
}

interface Props {
  flow: GiftCardModalFlow
  giftCardType: GiftCardType
  giftCard: GiftCard
  setGiftCard: React.Dispatch<React.SetStateAction<GiftCard | null>>
  setState: React.Dispatch<React.SetStateAction<GiftCardModalState>>
  onClose: () => void
}

interface GiftCardSellingProps extends Props {
  flow: 'SELLING'
}
export type PaymentState = 'started' | 'completed'
interface GiftCardPaymentProps extends Props {
  flow: 'REDEEMING'
  amount: string
  paymentType: ApiRetailerPaymentType
  onSuccess: (result: SalePayment) => void
  onPayment: (paymentState: PaymentState) => void
}

const BALANCES_ENDPOINT = '/api/2.0/balances'

const postGiftCardTransaction = async (
  giftCardNumber: string,
  amount: string
) => {
  const data = {
    amount: Number(amount) * -1,
    type: 'REDEEMING',
  }
  const response = await fetchApi(
    `${BALANCES_ENDPOINT}/gift_cards/${giftCardNumber}/transactions`,
    {
      method: 'POST',
      body: JSON.stringify(data),
      headers: {
        'Content-Type': 'application/json',
      },
    }
  )

  if (!response.ok) {
    throw new Error('Failed to post gift card transaction')
  }

  return await response.json()
}

export const ViewDetails: React.FC<
  GiftCardSellingProps | GiftCardPaymentProps
> = props => {
  const { flow, giftCardType, giftCard, setGiftCard, setState, onClose } = props
  const t = useT()
  const [hydratedTransactions, setHydratedTransactions] = useState<
    HydratedGiftCardTransaction[]
  >([])
  const {
    recordsTransactions,
    giftCardCapitalised,
  } = useGiftCardProviderSettings(flow, giftCardType)
  const currentOutlet = useRegisterManager().getActiveOutlet()
  const userManager = useUserManager()
  const formatCurrencyValue = useFormatCurrencyValue()
  const getDisplayTransactionType = useGetDisplayTransactionType()
  const {
    addFundsText,
    findDifferentText,
    payFullText,
    payPartialText,
    payingWithText,
  } = useViewDetailsStrings(giftCardType)
  const { inclusivePrice } = useAppSelector(selectSaleFinals)

  const displayStatus = useDisplayStatus(giftCard.status)
  const { payWithGiftCard } = useBigCommerceGiftCerts()

  const [isRedeeming, setIsRedeeming] = React.useState(false)

  const { isBalanceSufficient, getRedeemMessages } = useRedeemGiftCard()
  // Hydrate transactions on mount
  useEffect(() => {
    const getHydratedTransactions = async () => {
      const transactionsWithUser = await userManager.hydrateUsers(
        giftCard.transactions
      )

      const hydratedTransactions = calculateAndSortTransactionsBalance(
        transactionsWithUser
      )
      setHydratedTransactions(hydratedTransactions)
    }

    getHydratedTransactions()
  }, [giftCard.transactions, userManager])

  const lastTransaction =
    hydratedTransactions.length > 0 ? hydratedTransactions[0].createdAt : null

  const lastUsed = lastTransaction
    ? formatDateDistance(new Date(lastTransaction))
    : null

  const isRechargeable =
    giftCard.status !== 'VOIDED' && giftCard.status !== 'EXPIRED'

  const onFindDifferent = () => {
    setGiftCard(null)
    setState('SEARCH')
  }

  /**
   * Add funds to gift card in selling flow.
   */
  const handleAddFunds: MouseEventHandler<HTMLButtonElement> = e => {
    if (flow !== 'SELLING') {
      return
    }
    setState('ADD_BALANCE')
  }

  /**
   * Make payment with gift card for redeeming flow.
   * Applied to both bigcommerce gift certificates and Vend gift cards.
   */
  const handlePay: MouseEventHandler<HTMLButtonElement> = async e => {
    if (flow !== 'REDEEMING') {
      onClose()
      return
    }

    const { onSuccess, onPayment, paymentType, amount: amountToPay } = props
    if (vn(amountToPay).lte(0)) {
      ToastNotifications.negative(
        <T
          _str="Please enter a positive amount."
          _context="Message when amont to be paid from gift card is negative"
        />
      )
      onClose()
      return
    }

    const isPartialPay = !isBalanceSufficient(giftCard, Number(props.amount))
    const amount = isPartialPay ? giftCard.balance.toString() : amountToPay

    setIsRedeeming(true)
    onPayment('started')

    // Calculate the change amount. It is still possible to overpay using a
    // gift card, which is not ideal as it can be a vector for fraud, but until
    // this is addressed, we need to account for it.
    const changeAmount = vn(amount).gt(inclusivePrice)
      ? vn(amountToPay).minus(inclusivePrice).toString()
      : undefined

    if (giftCardType === 'BIGCOMMERCE') {
      const transactionData = await payWithGiftCard(giftCard, Number(amount))
      if (transactionData) {
        const payment: SalePayment = {
          paymentAmount: amount,
          paymentType,
          transactionData,
          changeAmount,
        }
        onSuccess(payment)
      }
    } else {
      postGiftCardTransaction(giftCard.code, amount)
        .then(result => {
          // You might think that it would be simpler to pass result here as the
          // transactionData. In fact, at least 2 people already did that, so
          // it's not surprising you think that. But really, you will cause an
          // error when we complete the sale so don't.
          const payment: SalePayment = {
            paymentAmount: amount,
            paymentType,
            transactionData: {
              // Some data is not included in payment requests if we
              // send the source as 'x-series'.
              source:
                result.source === 'x-series' ? 'Vend Gift Card' : result.source,
              source_id: result.id,
            },
            changeAmount,
          }
          onSuccess(payment)
        })
        .catch(() => {
          ToastNotifications.negative(
            <T
              _str="Sorry, something went wrong. Please try again."
              _context="Message when redeeming gift card fails"
            />
          )
        })
    }
    setIsRedeeming(false)
    onPayment('completed')
    onClose()
    return
  }

  let redeemMessages = null
  let isPartialPay = false
  let showPayButton = false
  if (flow === 'REDEEMING') {
    redeemMessages = getRedeemMessages(
      giftCard,
      giftCardType,
      props.amount,
      flow
    )
    showPayButton = !!vn(giftCard.balance).gt(0)
    isPartialPay = !isBalanceSufficient(giftCard, Number(props.amount))
  }

  return (
    <>
      <ModalHeader>
        <div className="vd-flex vd-flex--column vd-flex--align-center">
          <div className="gift-cards-details-header">
            {giftCardCapitalised}
            <span className="vd-ml1">{giftCard.code}</span>
            {giftCard.status !== 'ACTIVE' && (
              <Flag
                content={displayStatus}
                className="vd-ml1 vd-flag vd-flag--secondary"
              />
            )}
          </div>
          {recordsTransactions && lastUsed ? (
            <div className="vd-dialog-header-meta">
              {t('Last used {lastUsed}', {
                lastUsed,
                context: 'Last used date of the gift card',
              })}
            </div>
          ) : null}

          {redeemMessages?.balanceNotEnoughMsg && (
            <div className="vd-align-center wr-input-messages gift-cards-details-header-error">
              <T _str={redeemMessages.balanceNotEnoughMsg} />
            </div>
          )}
          {redeemMessages?.noBalanceMsg && (
            <div className="vd-align-center wr-input-messages gift-cards-details-header-error">
              <Icon icon="fa-exclamation-triangle" />
              &nbsp;
              <T _str={redeemMessages.noBalanceMsg} />
            </div>
          )}
        </div>
        <hr className="vd-dialog-header-divider" />
      </ModalHeader>

      <ModalContent scrollable>
        <ul className="wr-info-fields wr-info-fields--large wr-info-fields--center vd-mb5">
          <li className="wr-info-field">
            <div className="wr-info-field-label">
              <T _str="Balance" _context="Gift card balance label" />
            </div>
            <div className="wr-info-field-value">
              {formatCurrencyValue(giftCard.balance)}
            </div>
          </li>
          {!!giftCard.expiresAt && (
            <li className="wr-info-field">
              <div className="wr-info-field-label">
                <T _str="Expiry date" _context="Gift card expiry date label" />
              </div>
              <div className="wr-info-field-value">
                {formatDateTime(giftCard.expiresAt, {
                  ...DATE_MEDIUM,
                  timeZone: currentOutlet.time_zone,
                })}
              </div>
            </li>
          )}
        </ul>

        {recordsTransactions && (
          <Table>
            <TableHead>
              <TableRow>
                <TableHeadCell className="vd-sml-pad-h vd-pl0">
                  <T _str="Date" _context="Gift card transaction date label" />
                </TableHeadCell>
                <TableHeadCell className="vd-sml-pad-h vd-hide-mobile">
                  <T _str="User" _context="Gift card transaction user label" />
                </TableHeadCell>
                <TableHeadCell className="vd-sml-pad-h">
                  <T
                    _str="Status"
                    _context="Gift card transaction status label"
                  />
                </TableHeadCell>
                <TableHeadCell className="vd-sml-pad-h vd-align-right">
                  <T
                    _str="Total"
                    _context="Gift card transaction total label"
                  />
                </TableHeadCell>
                <TableHeadCell className="vd-sml-pad-h vd-pr0 vd-align-right vd-hide-mobile">
                  <T
                    _str="Balance"
                    _context="Gift card transaction balance label"
                  />
                </TableHeadCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {hydratedTransactions.map(transaction => (
                <TableRow key={transaction.id}>
                  <TableBodyCell className="vd-pl0 vd-sml-pad-h">
                    {formatDateTime(transaction.createdAt, {
                      ...DATE_TIME_SHORT,
                      timeZone: currentOutlet.time_zone,
                    })}
                  </TableBodyCell>
                  <TableBodyCell className="vd-sml-pad-h vd-hide-mobile">
                    {transaction.user?.displayName ||
                      transaction.user?.username ||
                      '-'}
                  </TableBodyCell>
                  <TableBodyCell className="vd-sml-pad-h">
                    {getDisplayTransactionType(transaction.type)}
                  </TableBodyCell>
                  <TableBodyCell className="vd-sml-pad-h vd-align-right">
                    {formatCurrencyValue(transaction.amount)}
                  </TableBodyCell>
                  <TableBodyCell className="vd-sml-pad-h vd-pr0 vd-align-right vd-hide-mobile">
                    {formatCurrencyValue(transaction.balance)}
                  </TableBodyCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </ModalContent>

      <ModalActions className="vd-btn-group">
        {!isRedeeming && (
          <Button
            variant="supplementary"
            name="Cancel"
            onClick={onFindDifferent}
          >
            {findDifferentText}
          </Button>
        )}
        {flow === 'SELLING' && isRechargeable ? (
          <Button variant="go" name="Add funds" onClick={handleAddFunds}>
            {addFundsText}
          </Button>
        ) : null}
        {flow === 'REDEEMING' && showPayButton && !isRedeeming && (
          <Button variant="go" name="Pay with gift card" onClick={handlePay}>
            {isPartialPay ? payPartialText : payFullText}
          </Button>
        )}
        {flow === 'REDEEMING' && isRedeeming && (
          <div className="gift-cards-loading-text">{payingWithText}</div>
        )}
      </ModalActions>
    </>
  )
}
